import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Buildings_Get_All, Buildings_Update, Buildings_Delete, Buildings_Restore_Single, Buildings_Restore_Cascade } from '../api/API_Buildings';
import { Floors_Get_All, Floors_Update, Floors_Delete, Floors_Restore_Single, Floors_Restore_Cascade } from '../api/API_Floors';
import { Rooms_Get_All, Rooms_Update, Rooms_Delete, Rooms_Restore_Single } from "../api/API_Rooms";
import { useStatus } from "./Context_Status_Global";
import { useMapRenderer } from "./Context_MapRenderer";
import { Context_UI } from "./Context_UI";

const Context_MapEditor = createContext();

export default function Context_Provider_MapEditor({ children }) {
    const [Buildings, Buildings_Set] = useState([]);
    const [Floors, Floors_Set] = useState([]);
    const [Rooms, Rooms_Set] = useState([]);
    const [Version, Version_Set] = useState(0);
    const [Map_Composition, Map_Composition_Set] = useState([]);
    const [Window_ActiveTab, Window_ActiveTab_Set] = useState("Building");
    const { Status_Saving_Set, Status_Fetching_Set, Status_Refresh_Set, List_Unsaved, List_Unsaved_Set, Confirmer_Confirm } = useStatus();
    const { Map_Refresh, Map_Information, Map_Information_Set, Information_Display } = useMapRenderer();
    const [Map_MovableMarkers, Map_MovableMarkers_Set] = useState(false);
    const [Map_Editing, Map_Editing_Set] = useState(false);
    const [Map_Editing_Mode, Map_Editing_Mode_Set] = useState("");
    const Location = useLocation();

    const UI = useContext(Context_UI);

    useEffect(() => {
        Buildings_Get_All()
            .then(Results => {
                Buildings_Set(Results.data.filter(Building => !Building.isDeleted));
            });
        Floors_Get_All()
            .then(Results => {
                Floors_Set(Results.data.filter(Floor => !Floor.isDeleted));
            });
        Rooms_Get_All()
            .then(Results => {
                Rooms_Set(Results.data.filter(Room => !Room.isDeleted));
            });
        // Composition_Build();
    }, [Version]);

    const Refresh = () => {
        Map_Refresh();
        Version_Set(prev => prev + 1);
    }

    const Composition_Build = () => {
        const Working_Composition = Buildings.map(Building => ({
            ...Building,
            Floors: []
        }));

        for (let Floor of Floors) {
            const Index = Working_Composition.findIndex(b => b._id === Floor.Building);
            if (Index !== -1) {
                Working_Composition[Index].Floors.push({ ...Floor, Rooms: [] });
            }
        }

        for (let Room of Rooms) {
            for (let Building of Working_Composition) {
                const Index_Floor = Building.Floors.findIndex(Floor => Floor._id === Room.Location.Floor);
                if (Index_Floor !== -1) {
                    const Index_Building = Working_Composition.findIndex(Building => Building._id === Room.Location.Building);
                    const Object_Floor = Working_Composition[Index_Building]?.Floors.find(Floor => Floor._id === Room.Location.Floor);
                    if (Object_Floor) {
                        Object_Floor.Rooms.push(Room);
                    }
                }
            }
        }

        Map_Composition_Set(Working_Composition);
        // console.log("Built composition:", Working_Composition);
    }

    const Building_Update = async (ID, Data) => {
        Status_Saving_Set("InProgress");
        try {
            await Buildings_Update(ID, Data);
            Status_Saving_Set("Success");
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        } finally {
            if (List_Unsaved.includes("Map Editor")) {
                List_Unsaved_Set(Previous => Previous.filter(Item => Item !== "Map Editor"));
            }
            Information_Display("Building", ID);
            Map_Editing_Set(false);

        }
    }

    const Floor_Update = async (ID, Data) => {
        Status_Saving_Set("InProgress");
        try {
            await Floors_Update(ID, Data);
            Status_Saving_Set("Success");
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        } finally {
            if (List_Unsaved.includes("Map Editor")) {
                List_Unsaved_Set(Previous => Previous.filter(Item => Item !== "Map Editor"));
            }
            Information_Display("Floor", ID);
            Map_Editing_Set(false);
        }
    }

    const Room_Update = async (ID, Data) => {
        Status_Saving_Set("InProgress");
        try {
            await Rooms_Update(ID, Data);
            Status_Saving_Set("Success");
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        } finally {
            if (List_Unsaved.includes("Map Editor")) {
                List_Unsaved_Set(Previous => Previous.filter(Item => Item !== "Map Editor"));
            }
            Information_Display("Room", ID);
            Map_Editing_Set(false);
        }
    }

    const Item_Delete_Confirm = async (Type) => {
        // UI.Subwindows_Open("Window_MapEditor_RippleDelete");
        const { Response } = await Confirmer_Confirm('Window_MapEditor_RippleDelete');
        if (Response === "Confirmed") {
            window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", "Item had been deleted.", "");
            if (Type == "Building") {
                Building_Delete();
            }
            if (Type == "Floor") {
                Floor_Delete();
            }
            if (Type == "Room") {
                Room_Delete();
            }
        } else {
            // window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", "Rejected", "");
        }

    }

    const Building_Delete = async () => {
        // console.log("Attempting delete...");
        Status_Saving_Set("InProgress");
        try {
            await Buildings_Delete(Map_Information._id);
            Status_Saving_Set("Success");
            Map_Information_Set(prev => ({ ...prev, isDeleted: true }));
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    const Floor_Delete = async () => {
        // console.log("Attempting delete...");
        Status_Saving_Set("InProgress");
        try {
            await Floors_Delete(Map_Information._id);
            Status_Saving_Set("Success");
            Map_Information_Set(prev => ({ ...prev, isDeleted: true }));
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    const Room_Delete = async () => {
        // console.log("Attempting delete...");
        Status_Saving_Set("InProgress");
        try {
            await Rooms_Delete(Map_Information._id);
            Status_Saving_Set("Success");
            Map_Information_Set(prev => ({ ...prev, isDeleted: true }));
            Refresh();
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    const Item_Restore_Confirm = async (Type) => {
        // UI.Subwindows_Open("Window_MapEditor_RippleDelete");
        const { Response, Data } = await Confirmer_Confirm('Window_MapEditor_Restore');
        if (Response === "Confirmed") {
            window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", `${Data.Type}`, "");
            if (Type == "Building") {
                Building_Restore(Data.Type);
            }
            if (Type == "Floor") {
                Floors_Restore(Data.Type);
            }
            if (Type == "Room") {
                Rooms_Restore(Data.Type);
            }
            // if (Type == "Building") {
            //     Building_Delete();
            // }
            // if (Type == "Floor") {
            //     Floor_Delete();
            // }
            // if (Type == "Room") {
            //     Room_Delete();
            // }
        } else {
            // window.Toasts_CreateToast("https://elmerf5445.github.io/Digi-Rooms-Status/Assets/Icons/favicon.png", "Rejected", "");
        }

    }

    const Building_Restore = async (Type) => {
        // console.log("Attempting restore...");
        Status_Saving_Set("InProgress");
        try {
            if (Type == "Single") {
                await Buildings_Restore_Single(Map_Information._id);
                Status_Saving_Set("Success");
                Map_Information_Set(prev => ({ ...prev, isDeleted: false }));
                Map_Editing_Mode_Set("");
                Refresh();
            } else if (Type == "Cascade") {
                await Buildings_Restore_Cascade(Map_Information._id);
                Map_Information_Set(prev => ({ ...prev, isDeleted: false }));
                Map_Editing_Mode_Set("");
                Status_Saving_Set("Success");
                Refresh();
            }

        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    const Floors_Restore = async (Type) => {
        // console.log("Attempting restore...");
        Status_Saving_Set("InProgress");
        try {
            if (Type == "Single") {
                await Floors_Restore_Single(Map_Information._id);
                Status_Saving_Set("Success");
                Map_Information_Set(prev => ({ ...prev, isDeleted: false }));
                Map_Editing_Mode_Set("");
                Refresh();
            } else if (Type == "Cascade") {
                await Floors_Restore_Cascade(Map_Information._id);
                Map_Information_Set(prev => ({ ...prev, isDeleted: false }));
                Map_Editing_Mode_Set("");
                Status_Saving_Set("Success");
                Refresh();
            }

        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    const Rooms_Restore = async (Type) => {
        // console.log("Attempting restore...");
        Status_Saving_Set("InProgress");
        try {
            if (Type == "Single") {
                await Rooms_Restore_Single(Map_Information._id);
                Map_Information_Set(prev => ({ ...prev, isDeleted: false }));
                Map_Editing_Mode_Set("");
                Status_Saving_Set("Success");
                Refresh();
            }
        } catch (Error) {
            console.error(Error);
            Status_Saving_Set("Error");
        }
    }

    useEffect(() => {
        switch (Location.pathname) {
            case "/map/edit/add/building":
                Refresh();
                Window_ActiveTab_Set("Tab_Building");
                UI.Subwindows_Open("Window_MapEditor_Add");
                break;
            case "/map/edit/add/floor":
                Refresh();
                Window_ActiveTab_Set("Tab_Floor");
                UI.Subwindows_Open("Window_MapEditor_Add");
                break;
            case "/map/edit/add/room":
                Refresh();
                Window_ActiveTab_Set("Tab_Room");
                UI.Subwindows_Open("Window_MapEditor_Add");
                break;
            case "/map/edit":
            case "/map/edit/table":
            case "/map/edit/bin":
                if (document.getElementById("Window_MapEditor_Add")) {
                    UI.Subwindows_Close("Window_MapEditor_Add");
                }
                break;
        }
    }, [Location.pathname]);

    return (
        <Context_MapEditor.Provider value={{
            Buildings,
            Buildings_Set,
            Floors,
            Floors_Set,
            Rooms,
            Rooms_Set,
            Version,
            Refresh,
            Map_Composition,
            Map_Composition_Set,
            Composition_Build,
            Window_ActiveTab,
            Window_ActiveTab_Set,
            Building_Update,
            Floor_Update,
            Room_Update,
            Map_MovableMarkers_Set,
            Map_MovableMarkers,
            Map_Editing,
            Map_Editing_Set,
            Item_Delete_Confirm,
            Map_Editing_Mode,
            Map_Editing_Mode_Set,
            Item_Restore_Confirm
        }}>
            {children}
        </Context_MapEditor.Provider>
    )
}

export function useMapEditor() {
    return useContext(Context_MapEditor);
}